import React from "react";
import AccordionSummary from "@mui/material/AccordionSummary";
import {
  Accordion,
  IconButton,
  Card,
  Typography,
  Tooltip,
  FormControlLabel,
  Switch,
  Chip,
} from "@mui/material";
import Div from "@jumbo/shared/Div";
import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Field } from "formik";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { stringToBoolean } from "app/utils/appHelpers";

const DSOKProductItem = ({
  item,
  currencyCode,
  disabled,
  onDelete,
  onEdit,
  onCopy,
  onToggleActive,
  defaultLanguage,
  productCategories,
  selectMode,
  onItemUpdate,
  selectedProducts,
}) => {
  const { t, i18n } = useTranslation();
  const handleEditClick = (event) => {
    event?.stopPropagation?.();
    if (!disabled && typeof onEdit === "function") {
      onEdit(event);
    }
  };

  const getLocalizedText = (value) => {
    if (typeof value === "string") return value;
    if (value && typeof value === "object") {
      const lang = defaultLanguage || i18n?.language;
      if (lang && typeof value[lang] === "string" && value[lang].trim()) {
        return value[lang];
      }
      const fallbacks = ["en", "sk", "cs", "de"];
      for (const fb of fallbacks) {
        if (typeof value[fb] === "string" && value[fb].trim()) return value[fb];
      }
      const firstStr = Object.values(value).find(
        (v) => typeof v === "string" && v.trim(),
      );
      if (firstStr) return firstStr;
    }
    return "";
  };

  const categoryNames =
    productCategories
      ?.map((category) => getLocalizedText(category?.name))
      .filter((name) => name) || [];
  let productImage = "/images/noImagePlaceholder.png";
  const randomNumber = Math.random();
  if (item?.image) {
    productImage = item?.image + "?random=" + randomNumber;
  }
  if (
    item?.customProductImageUrl &&
    item?.customProductImageUrl !== null &&
    item?.customProductImageUrl !== undefined
  ) {
    productImage = item?.customProductImageUrl + "?random=" + randomNumber;
  }
  return (
    <Card sx={{ mb: 1 }}>
      <Accordion expanded={false} square sx={{ borderRadius: 2 }}>
        <AccordionSummary
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          sx={{
            px: 3,
            flexDirection: "row-reverse",
            "& .MuiAccordionSummary-content": {
              alignItems: "center",
              justifyContent: "space-between",

              "&.Mui-expanded": {
                margin: "12px 0",
              },
            },
            ".MuiAccordionSummary-expandIconWrapper": {
              color: "text.secondary",
              transform: "none",
              height: 28,
              width: 28,
              alignItems: "center",
              justifyContent: "center",
              mr: 1,

              "&.Mui-expanded": {
                transform: "none",
                color: "primary.main",
                borderColor: "primary.main",
              },

              "& svg": {
                fontSize: "1.25rem",
              },
            },
          }}
        >
          <Div
            sx={{ flex: { xs: "0 1 auto", md: 0.25 }, flexShrink: 0, px: 1 }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.products.image")}
            </Typography>
            <Typography
              variant={"body1"}
              onClick={handleEditClick}
              sx={{ cursor: disabled ? "default" : "pointer" }}
            >
              <img
                src={productImage}
                width={75}
                alt={
                  getLocalizedText(item?.productName) ||
                  getLocalizedText(item?.name) ||
                  "Product Image"
                }
                style={{ cursor: disabled ? "default" : "pointer" }}
              />
            </Typography>
          </Div>
          <Div sx={{ flex: { xs: "0 1 auto", md: 1 }, flexShrink: 0, px: 1 }}>
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.presentation.label")}
            </Typography>
            <Div>
              <Typography
                variant={"body1"}
                onClick={handleEditClick}
                sx={{
                  cursor: disabled ? "default" : "pointer",
                  "&:hover": disabled
                    ? undefined
                    : {
                        textDecoration: "underline",
                      },
                }}
              >
                {getLocalizedText(item?.productName) ||
                  getLocalizedText(item?.name)}
              </Typography>
              <Div sx={{ mt: 0.25 }}>
                <Typography
                  variant={"caption"}
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem", lineHeight: 1.2 }}
                >
                  ID: {item?._id}
                </Typography>
                {item?.sku && String(item.sku) !== String(item?._id) && (
                  <Typography
                    variant={"caption"}
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem", lineHeight: 1.2, ml: 1 }}
                  >
                    SKU: {item.sku}
                  </Typography>
                )}
                {item?.customProductId && (
                  <Typography
                    variant={"caption"}
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem", lineHeight: 1.2, ml: 1 }}
                  >
                    Custom ID: {item.customProductId}
                  </Typography>
                )}
                {item?.additionalInformation?.cashRegisterCode && (
                  <Typography
                    variant={"caption"}
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem", lineHeight: 1.2, ml: 1 }}
                  >
                    PLU: {item.additionalInformation.cashRegisterCode}
                  </Typography>
                )}
              </Div>
              {stringToBoolean(item?.isGlutenFree) && (
                <Div sx={{ mt: 0.5 }}>
                  <Chip
                    label={t("pages.dsokProducts.glutenFree", {
                      defaultValue: "Bezlepkové",
                    })}
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ height: 20, fontSize: "0.65rem" }}
                  />
                </Div>
              )}
            </Div>
          </Div>
          <Div sx={{ flex: { xs: "0 1 auto", md: 0.5 }, flexShrink: 0, px: 1 }}>
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.screens.status")}
            </Typography>
            <Tooltip
              title={
                stringToBoolean(item?.isActive)
                  ? t("pages.products.active")
                  : t("pages.products.disabled")
              }
              placement="top"
            >
              <Switch
                checked={stringToBoolean(item?.isActive)}
                onChange={(e) => {
                  e.stopPropagation();
                  onToggleActive?.(item);
                }}
                onClick={(e) => e.stopPropagation()}
                disabled={disabled}
                size="small"
              />
            </Tooltip>
          </Div>
          {!selectMode && (
            <>
              <Div
                sx={{ flex: { xs: "0 1 auto", md: 0.5 }, flexShrink: 0, px: 1 }}
              >
                <Typography
                  fontSize={"12px"}
                  variant={"h6"}
                  color={"text.secondary"}
                  mb={0.25}
                  sx={{
                    display: { xs: "none", md: "block" },
                  }}
                >
                  {t("pages.dsokCategories.category")}
                </Typography>
                <Div
                  sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}
                >
                  {categoryNames.length > 0 ? (
                    categoryNames.map((name, index) => (
                      <Typography
                        key={index}
                        variant={"body2"}
                        sx={{ fontSize: "0.875rem", lineHeight: 1.2 }}
                      >
                        {name}
                      </Typography>
                    ))
                  ) : (
                    <Typography
                      variant={"body2"}
                      sx={{ fontSize: "0.875rem", color: "text.secondary" }}
                    >
                      —
                    </Typography>
                  )}
                </Div>
              </Div>
            </>
          )}
          <Div
            sx={{
              flex: { xs: "0 1 auto", md: 0.5 },
              flexShrink: 0,
              px: 1,
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.products.price")}
            </Typography>
            <Typography variant={"body1"} sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
              <span>{Number(item?.price).toFixed(2)} {currencyCode || ""}</span>
              {item?.vatRate ? (
                <span style={{ color: "#7b1fa2", fontSize: "0.8em" }}>
                  [{item.vatRate}%]
                </span>
              ) : null}
            </Typography>
          </Div>
          {selectMode && (
            <Div
              sx={{
                flex: { xs: "0 1 auto", md: 0.75 },
                flexShrink: 0,
                px: 1,
              }}
            >
              <Typography
                fontSize={"12px"}
                variant={"h6"}
                color={"text.secondary"}
                mb={0.25}
                sx={{
                  display: { xs: "none", md: "block" },
                }}
              >
                {t("pages.dsokProducts.takeaway")}
              </Typography>
              <Div sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FormControlLabel
                  control={
                    <Field
                      component={Switch}
                      onChange={(e) =>
                        onItemUpdate(item._id, "isTakeaway", e.target.checked)
                      }
                    />
                  }
                  checked={selectedProducts[item._id]?.isTakeaway || false}
                />
                {selectedProducts[item._id]?.isTakeaway && (
                  <Div sx={{ minWidth: 120 }}>
                    <JumboTextField
                      fullWidth
                      name="null"
                      label={t("pages.products.vatRateTakeaway")}
                      InputLabelProps={{ required: false }}
                      value={selectedProducts[item._id]?.vatRateTakeaway ?? ""}
                      onChange={(e) =>
                        onItemUpdate(
                          item._id,
                          "vatRateTakeaway",
                          Number(e.target.value),
                        )
                      }
                    />
                  </Div>
                )}
              </Div>
            </Div>
          )}
          {selectMode && (
            <>
              <Div
                sx={{
                  flex: { xs: "0 1 auto", md: 0.25 },
                  flexShrink: 0,
                  px: 1,
                }}
              >
                <JumboTextField
                  type="number"
                  fullWidth
                  name="null"
                  defaultValue={selectedProducts[item._id]?.customPrice || 0}
                  value={selectedProducts[item._id]?.customPrice || 0}
                  label={t("pages.dsokProducts.customPrice")}
                  InputLabelProps={{ required: false }}
                  InputProps={{
                    inputProps: {
                      min: 0,
                      step: 0.01,
                    },
                    onChange: (e) =>
                      onItemUpdate(item._id, "customPrice", e.target.value),
                  }}
                />
              </Div>
              <Div
                sx={{
                  flex: { xs: "0 1 auto", md: 0.25 },
                  flexShrink: 0,
                  px: 1,
                }}
              >
                <JumboTextField
                  type="number"
                  fullWidth
                  name="null"
                  defaultValue={selectedProducts[item._id]?.qty || 0}
                  value={selectedProducts[item._id]?.qty || 0}
                  label={t("pages.dsokOrders.quantity")}
                  InputLabelProps={{ required: false }}
                  InputProps={{
                    inputProps: {
                      min: 0,
                    },
                    onChange: (e) =>
                      onItemUpdate(item._id, "qty", e.target.value),
                  }}
                />
              </Div>
              <Div
                sx={{
                  flex: { xs: "0 1 auto", md: 0.25 },
                  flexShrink: 0,
                  px: 1,
                }}
              >
                <Typography
                  fontSize={"12px"}
                  variant={"h6"}
                  color={"text.secondary"}
                  mb={0.25}
                  sx={{
                    display: { xs: "none", md: "block" },
                  }}
                >
                  {t("pages.dsokProducts.autoAdd")}
                </Typography>
                <Div sx={{ mt: 0, mb: 0, mx: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Field
                        component={Switch}
                        onChange={(e) =>
                          onItemUpdate(item._id, "autoAdd", e.target.checked)
                        }
                      />
                    }
                    checked={selectedProducts[item._id]?.autoAdd || false}
                  />
                </Div>
              </Div>
              <Div
                sx={{ flex: { xs: "0 1 auto", md: 0.5 }, flexShrink: 0, px: 1 }}
              >
                <Typography
                  fontSize={"12px"}
                  variant={"h6"}
                  color={"text.secondary"}
                  mb={0.25}
                  sx={{
                    display: { xs: "none", md: "block" },
                  }}
                >
                  {t("pages.dsokProducts.isRequired")}
                </Typography>
                <Div sx={{ mt: 0, mb: 0, mx: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Field
                        component={Switch}
                        onChange={(e) =>
                          onItemUpdate(item._id, "isRequired", e.target.checked)
                        }
                      />
                    }
                    checked={selectedProducts[item._id]?.isRequired || false}
                  />
                </Div>
              </Div>
            </>
          )}
          <Div
            sx={{
              flex: { xs: "0 2 auto", md: 0 },
              px: 1,
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {selectMode
                ? t("pages.products.active")
                : t("pages.presentation.actions")}
            </Typography>
            <Div
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 0.5,
                minWidth: 70,
              }}
            >
              {selectMode ? (
                <FormControlLabel
                  control={
                    <Field
                      // name={`include_in_menu`}
                      component={Switch}
                      onChange={(e) =>
                        onItemUpdate(item._id, "status", e.target.checked)
                      }
                    />
                  }
                  checked={selectedProducts[item._id]?.status || false}
                />
              ) : (
                <>
                  <IconButton
                    size="small"
                    variant={"contained"}
                    disabled={disabled}
                    onClick={onEdit}
                  >
                    <Tooltip title={t("buttons.edit")} placement="top-start">
                      <EditIcon />
                    </Tooltip>
                  </IconButton>
                  <IconButton
                    size="small"
                    variant={"contained"}
                    disabled={disabled}
                    onClick={() => onCopy(item)}
                  >
                    <Tooltip title={t("buttons.copy")} placement="top-start">
                      <ContentCopyIcon />
                    </Tooltip>
                  </IconButton>
                  <IconButton
                    size="small"
                    variant={"contained"}
                    disabled={disabled}
                    onClick={() => onDelete(item)}
                  >
                    <Tooltip title={t("buttons.delete")} placement="top-start">
                      <DeleteForeverIcon />
                    </Tooltip>
                  </IconButton>
                </>
              )}
            </Div>
          </Div>
        </AccordionSummary>
      </Accordion>
    </Card>
  );
};
/* Todo item prop define */
export default DSOKProductItem;
